import {
  DefaultButton,
  DialogFooter,
  IconButton,
  Label,
  mergeStyleSets,
} from "@fluentui/react";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import ErrorMsg from "components/controls/ErrorMsg";
import NumberInputField from "components/controls/NumberInputField";
import { EActionDialog } from "pages/fleet-form/FleetForm/constants";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";

// One limit as edited: the amounts (CZK) as digit strings (NumberInputField).
export interface LimitRow {
  personalInjury: string;
  propertyDamage: string;
}

export const EMPTY_LIMIT_ROW: LimitRow = { personalInjury: "", propertyDamage: "" };

// Formik field (and flat error key) of a limit's amount.
export const limitFieldName = (index: number, field: keyof LimitRow) =>
  `limits.${index}.${field}`;

const MtplInsuranceLimitsFormFields = ({
  fieldProps,
  insurerOptions,
  isLoading,
}) => {
  const classes = getClassNames();
  const { t } = useTranslation();
  const { setFieldValue, values, errors } = fieldProps;
  const isReadOnly =
    useSelector((s) => s.fleetForm.dialog.type) === EActionDialog.ReadOnly;

  const limits: LimitRow[] = values.limits || [];

  const addLimit = () =>
    setFieldValue("limits", [...limits, { ...EMPTY_LIMIT_ROW }]);

  const removeLimit = (index: number) =>
    setFieldValue(
      "limits",
      limits.filter((_, i) => i !== index)
    );

  return (
    <>
      <div className={classes.row}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          <DatePickerField
            errors={errors}
            setFieldValue={setFieldValue}
            defaultValue={values.validFromDate}
            name={"validFromDate"}
            disabled={isReadOnly}
            label={"bfm.vehicleForm.validFromDate.label"}
            placeholder={t("bfm.selectDate")}
            required
            confidentiality={null}
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          <ComboBoxField
            id="insurerPartnerNumber"
            disabled={isReadOnly}
            errors={errors}
            required
            name="insurerPartnerNumber"
            label={"bfm.insurer.label"}
            setFieldValue={setFieldValue}
            options={insurerOptions || []}
            defaultValue={values?.insurerPartnerNumber}
            confidentiality={null}
          />
        </div>
      </div>

      <Label required className={classes.limitsLabel}>
        {t("bfm.mtplInsuranceLimits.label")}
      </Label>
      {limits.map((row, index) => (
        <div key={index} className={classes.row}>
          <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
            <NumberInputField
              setFieldValue={setFieldValue}
              value={row.personalInjury}
              errors={errors}
              name={limitFieldName(index, "personalInjury")}
              disabled={isReadOnly}
              required
              label={"bfm.mtplInsuranceLimits.personalInjury.label"}
            />
          </div>
          <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
            <NumberInputField
              setFieldValue={setFieldValue}
              value={row.propertyDamage}
              errors={errors}
              name={limitFieldName(index, "propertyDamage")}
              disabled={isReadOnly}
              required
              label={"bfm.mtplInsuranceLimits.propertyDamage.label"}
            />
          </div>
          <div className={classes.removeButton}>
            {/* at least one limit */}
            {!isReadOnly && limits.length > 1 && (
              <IconButton
                iconProps={{ iconName: "Cancel" }}
                title={t("greco.delete")}
                ariaLabel={t("greco.delete")}
                onClick={() => removeLimit(index)}
              />
            )}
          </div>
        </div>
      ))}
      {errors.limits && <ErrorMsg>{errors.limits}</ErrorMsg>}
      {!isReadOnly && (
        <DefaultButton
          className={classes.addButton}
          text={t("bfm.mtplInsuranceLimits.addLimit.label")}
          iconProps={{ iconName: "Add" }}
          onClick={addLimit}
        />
      )}

      <div className={classes.placeholder}></div>
      {!isReadOnly && (
        <DialogFooter className={classes.footer}>
          <DefaultButton
            id="saveButton"
            primary
            text={t("greco.save")}
            type="submit"
            iconProps={{
              iconName: "Save",
            }}
            disabled={isLoading}
          />
        </DialogFooter>
      )}
    </>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    row: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
    },
    fieldItem: {
      width: "100%",
      marginTop: 10,
    },
    limitsLabel: {
      marginTop: 20,
    },
    removeButton: {
      width: 40,
      flexShrink: 0,
      paddingTop: 38,
    },
    addButton: {
      marginTop: 15,
    },
    placeholder: {
      height: 100,
    },
    footer: {
      marginTop: 50,
    },
  });

export default MtplInsuranceLimitsFormFields;
